import Phaser from "phaser";
import { Away, type EntityState } from "odyssey-shared";
import type { NameplateChoices } from "../../settings.ts";
import type { IconSource } from "../../icons/page-icons.ts";
import { ICON_TEXTURE, iconFrame, iconTexture } from "../../icons/atlas-texture.ts";
import type { ReducedMotion } from "../../reduced-motion.ts";
import type { BubbleReport } from "../bubbles/report.ts";
import { barPenaltyOf, barShown } from "../health-bars/bar.ts";
import type { WorldUiModule } from "../hook.ts";
import { awayMarkShown } from "../nameplates/idle-mark.ts";
import { onScreen } from "../nameplates/layout.ts";
import { plateForm } from "../nameplates/look.ts";
import type { NameplateReport } from "../nameplates/report.ts";
import { emojiCell } from "./art.ts";
import { EMOJI_COPIES, copyMotion, copyPath, newCopyMotion, newCopyPath } from "./burst.ts";
import { CopyRing } from "./copies.ts";
import { emojiLayout, emojiScale, emojiStackTop, emojiStand, newEmojiLayout, type EmojiLayout } from "./layout.ts";
import { EMOJI_LIFE_MS, emojiFrame, emojiLives } from "./motion.ts";
import { EmojiPool } from "./pool.ts";
import { emojiMayShow } from "./shown.ts";
import { drawn, hide, place, type Drawn } from "./sprites.ts";
import type { EmojiCopyReading, EmojiPhase, EmojiReading, EmojiReport } from "./report.ts";

/**
 * Emoji over characters on the canvas (P2.19-T7 and P2.19-T16; ADR-114 decisions 5.2 to 5.8 and the amendment of
 * 2026-10-09): an emoji the server showed over a character pops out of the character's middle as 20 copies of its art
 * that scatter, drift up and fade (`motion.ts`, `burst.ts`), each an `Image` of the page's icon atlas drawn by the UI
 * camera with nearest filtering at a whole number of device pixels an art pixel (`layout.ts`), placed from the world
 * point the character's middle stood at. Under reduced motion it is one still `Image` instead, its foot over the
 * plate's stack or the character's bubble. One a character; a pool of 32 (`pool.ts`); the copies are a ring of 640
 * sprites every pop shares (`copies.ts`). It is drawn alone, never in a bubble.
 *
 * The page shows each `emojiShown` into the report's queue as an icon key (`art.ts`, `report.ts`), so anything that
 * names an emoji's key can show one; each frame the surface takes the waiting emoji, then visits the characters the
 * scene draws, then ends the emoji whose time ran out or whose character was not drawn, as a bubble goes when its
 * speaker leaves the view. An emoji shows only while its character is drawn and not hidden from the viewer (concealed
 * and not targeted, ADR-061's rule 4 for a surface drawn for an entity), stands inside the vision cap around the local
 * character, is the viewer's own or the viewer shows other players' emotes, its art is in the atlas, and it lies on the
 * canvas; while hidden its time still runs, so it shows if its character steps out within it. Its copies are drawn on
 * exactly the frames it may show, and end with it.
 *
 * The atlas is the page's (`page-icons.ts`), decoded once for the page: when it is ready the surface hands the decoded
 * image to the texture manager under `ICON_TEXTURE` (`icons/atlas-texture.ts`, shared with the ground items), filtered
 * nearest, and adds a frame for a key the first time an
 * emoji draws it. Until then nothing is drawn, since an emoji has no glyph to stand in for it.
 *
 * A frame of steady play allocates nothing: the emoji are taken and the characters visited through functions made
 * once, the projection, the layout and the motion write into objects made once, a pop writes its copies' paths into
 * the ring's typed arrays, and a sprite's frame, place and strength are set only when they change. With no emoji held
 * no character is visited.
 */

/**
 * The depth of every emoji sprite in the UI layer, over the bubbles' objects at 1 and the plates' at 0, and under the
 * interaction prompt's at 3, which an emoji never covers.
 */
export const EMOJI_DEPTH = 2;

/**
 * The Nameplates choices that draw every plate, for placing an emoji where it would stand with plates on (decision 5.2):
 * a health bar lifts the emoji as it would lift the plate, whatever the viewer chose.
 */
const PLATES_ON: Readonly<NameplateChoices> = Object.freeze({ others: "first", own: "on" });

/**
 * The most pops counted before the count starts again at 1, the largest token the copies' ring takes.
 */
const MAX_POP = 0x7fffffff;

/**
 * One emoji: its still sprite, the key it draws, when it started, the frame its character was last drawn on, its phase
 * when last visited ("waiting" until it pops), and its pop: the pop's token (0 before it popped), its first slot in the copies' ring, the world
 * point the character's middle stood at, and how many copies were drawn on the last frame.
 */
interface Emoji extends Drawn {
  key: string;
  startedAt: number;
  seen: number;
  phase: EmojiPhase;
  pop: number;
  copyFirst: number;
  popX: number;
  popY: number;
  copies: number;
}

/**
 * The emoji surface.
 *
 * @param report The game report's emoji entry: its switches, the queue of emoji shown, and where the pool's counts and
 *   readings go.
 * @param bubbles The bubbles' report, whose `topOf` says where a character's bubble stands; this surface is listed after
 *   the bubbles, so the bubble read is the frame's.
 * @param plates The nameplates' report: the viewer's Nameplates choices and Show idle and AFK marks choice, which say
 *   whether an idle mark stands over a plate.
 * @param motion The game's reduced motion, read each frame.
 * @param icons The page's icons, whose atlas the emoji are drawn from.
 * @returns The surface's module, listed after the bubbles.
 */
export function emoji(report: EmojiReport, bubbles: Pick<BubbleReport, "topOf">, plates: Readonly<NameplateReport>, motion: ReducedMotion, icons: IconSource): WorldUiModule {
  return {
    id: "emoji",
    /**
     * Builds the pool and the frame's taker and visitor, follows the page's icons, and lists the emoji for the report.
     * Nothing is drawn or made until the first emoji.
     *
     * @param context The UI layer, the projection and the characters the scene draws.
     * @returns The surface.
     */
    create(context) {
      const scene = context.scene;
      /**
       * Makes a hidden image in the UI layer at the emoji's depth.
       *
       * @returns The image.
       */
      const image = (): Phaser.GameObjects.Image => context.add(new Phaser.GameObjects.Image(scene, 0, 0, "__DEFAULT")).setOrigin(0, 0).setDepth(EMOJI_DEPTH).setVisible(false);
      const pool = new EmojiPool<Emoji>(() => ({ ...drawn(image()), key: "", startedAt: 0, seen: 0, phase: "waiting", pop: 0, copyFirst: 0, popX: 0, popY: 0, copies: 0 }));
      const ring = new CopyRing<Drawn>(() => drawn(image()));
      const local = { x: 0, y: 0 };
      const anchor = { x: 0, y: 0 };
      const layout: EmojiLayout = newEmojiLayout();
      const path = newCopyPath();
      const flying = newCopyMotion();
      const camera = context.worldCamera;
      let frameNumber = 0;
      let now = 0;
      let reduced = false;
      let shown = 0;
      let copiesShown = 0;
      let pops = 0;
      let ownHandle = -1;
      let texture: Phaser.Textures.Texture | null = null;

      /**
       * Hands the page's decoded atlas to the texture manager once it is ready, filtered nearest; an atlas the page did
       * not decode itself leaves the emoji undrawn.
       */
      const adopt = (): void => {
        texture ??= iconTexture(scene.textures, icons.getSnapshot().atlas);
      };
      const unsubscribe = icons.subscribe(adopt);
      adopt();

      /**
       * The frame an emoji's key draws, added to the atlas's texture the first time it is asked for.
       *
       * @param key The icon key.
       * @returns The frame's name, the key, or null while the atlas is not a texture or its pointer lacks the key.
       */
      const frameOf = (key: string): string | null => {
        return texture === null ? null : iconFrame(texture, key, () => emojiCell(icons.getSnapshot().atlas, key, icons.log));
      };

      /**
       * Starts or restarts a character's emoji for one taken from the queue, from the frame's time. One taken while the
       * switch is off, or naming a handle no snapshot carries, starts nothing.
       *
       * @param handle The character's handle.
       * @param key The icon key.
       */
      const start = (handle: number, key: string): void => {
        if (!report.enabled || !Number.isInteger(handle) || handle < 0 || handle > 0xffff) {
          return;
        }
        const held = pool.start(handle, now);
        endCopies(held);
        held.phase = "waiting";
        held.startedAt = now;
        held.seen = frameNumber;
        if (held.key !== key) {
          held.key = key;
          held.frame = "";
        }
        held.deviceLeft = Number.NaN;
      };

      /**
       * Hides every copy an emoji's pop still owns, leaving the pop in place.
       *
       * @param held The emoji.
       */
      const hideCopies = (held: Emoji): void => {
        held.copies = 0;
        if (held.pop === 0) {
          return;
        }
        for (let index = 0; index < EMOJI_COPIES; index += 1) {
          const slot = (held.copyFirst + index) % ring.size;
          if (ring.owns(slot, held.pop)) {
            hide(ring.sprite(slot));
          }
        }
      };

      /**
       * Hides an emoji's copies and forgets its pop, when it ends or starts again.
       *
       * @param held The emoji.
       */
      const endCopies = (held: Emoji): void => {
        hideCopies(held);
        held.pop = 0;
      };

      /**
       * Pops an emoji: counts the pop, keeps the world point its character's middle stands at, claims the ring's next
       * slots and writes each copy's seeded path and frame.
       *
       * @param held The emoji, its frame set.
       * @param handle Its character's handle.
       * @param worldX The character's feet across, in world pixels.
       * @param worldY The character's middle, in world pixels.
       */
      const pop = (held: Emoji, handle: number, worldX: number, worldY: number): void => {
        held.popX = worldX;
        held.popY = worldY;
        pops = pops >= MAX_POP ? 1 : pops + 1;
        held.pop = pops;
        held.copyFirst = ring.claim(EMOJI_COPIES, pops);
        for (let index = 0; index < EMOJI_COPIES; index += 1) {
          const slot = (held.copyFirst + index) % ring.size;
          ring.write(slot, copyPath(handle, pops, index, path));
          const copy = ring.sprite(slot);
          hide(copy);
          if (copy.frame !== held.frame) {
            copy.image.setTexture(ICON_TEXTURE, held.frame);
            copy.frame = held.frame;
          }
        }
      };

      /**
       * Draws the copies an emoji's pop still owns, from the world point it popped at, each on whole device pixels.
       *
       * @param held The emoji, popped.
       * @param scale The UI scale.
       */
      const drawCopies = (held: Emoji, scale: number): void => {
        context.project(held.popX, held.popY, anchor);
        const centreX = Math.round(anchor.x * scale);
        const centreY = Math.round(anchor.y * scale);
        const k = emojiScale(scale);
        const size = 32 * k;
        const frame = emojiFrame(now - held.startedAt);
        let count = 0;
        for (let index = 0; index < EMOJI_COPIES; index += 1) {
          const slot = (held.copyFirst + index) % ring.size;
          if (!ring.owns(slot, held.pop)) {
            continue;
          }
          const copy = ring.sprite(slot);
          if (!copyMotion(frame, ring.cos[slot] as number, ring.sin[slot] as number, ring.reach[slot] as number, ring.drift[slot] as number, ring.wait[slot] as number, ring.life[slot] as number, flying)) {
            hide(copy);
            continue;
          }
          const left = Math.round(centreX + flying.dx * scale) - size / 2;
          const top = Math.round(centreY + flying.dy * scale) - size / 2;
          if (!onScreen(left, top, size, size, camera.width, camera.height)) {
            hide(copy);
            continue;
          }
          place(copy, left, top, scale, k, flying.alpha);
          count += 1;
        }
        held.copies = count;
        copiesShown += count;
      };

      /**
       * Brings one drawn character's emoji, if it holds one, up to date for the frame.
       *
       * @param handle The character's handle.
       * @param x Its feet across, in world pixels.
       * @param y Its feet down, in world pixels.
       * @param state Its state, with its name, Health, buffs, away state and animation, which place the plate's stack.
       * @param head How far its art reaches above its feet, in world pixels.
       * @param hidden Whether its world UI hides (ADR-061's amendment for P1.9-T73): its emoji is kept, hidden, its time
       *   still running.
       */
      const visit = (handle: number, x: number, y: number, state: EntityState, head: number, hidden: boolean): void => {
        const held = pool.find(handle);
        if (held === undefined) {
          return;
        }
        held.seen = frameNumber;
        const own = handle === ownHandle;
        const alive = emojiLives(now - held.startedAt);
        held.phase = reduced ? "still" : held.pop === 0 ? "waiting" : "popped";
        if (!alive || !emojiMayShow(hidden, own, report.others, x - local.x, y - local.y)) {
          hide(held);
          hideCopies(held);
          return;
        }
        if (held.frame === "") {
          const frame = frameOf(held.key);
          if (frame === null) {
            hide(held);
            return;
          }
          held.image.setTexture(ICON_TEXTURE, frame);
          held.frame = frame;
        }
        const scale = context.scale.current;
        if (!reduced) {
          hide(held);
          if (held.pop === 0) {
            pop(held, handle, x, y - head / 2);
            held.phase = "popped";
          }
          drawCopies(held, scale);
          return;
        }
        hideCopies(held);
        const k = emojiScale(scale);
        context.project(x, y - head, anchor);
        const anchorX = Math.round(anchor.x * scale);
        const headY = Math.round(anchor.y * scale);
        const plateDraws = plateForm(own, plates) !== "none" && state.name.first !== "";
        const lifted = barShown(state.health, own, PLATES_ON, barPenaltyOf(state.buffs));
        const idle = awayMarkShown(state.away ?? Away.Active, state.anim, own, plateDraws, plates.idleMarks);
        emojiLayout(anchorX, emojiStand(emojiStackTop(headY, scale, lifted, idle), bubbles.topOf(handle)), scale, k, layout);
        if (!onScreen(layout.deviceLeft, layout.deviceTop, layout.deviceSize, layout.deviceSize, camera.width, camera.height)) {
          hide(held);
          return;
        }
        place(held, layout.deviceLeft, layout.deviceTop, scale, k, 1);
        shown += 1;
      };

      /**
       * Ends an emoji whose time ran out, or whose character was not drawn on the frame.
       *
       * @param held The emoji.
       * @param handle Its character's handle.
       */
      const sweep = (held: Emoji, handle: number): void => {
        if (held.seen !== frameNumber || now - held.startedAt >= EMOJI_LIFE_MS) {
          hide(held);
          endCopies(held);
          pool.end(handle);
        }
      };

      /**
       * Reads every emoji held, for the report: made on request, never on a frame.
       *
       * @returns One reading an emoji.
       */
      report.list = (): readonly EmojiReading[] => {
        const readings: EmojiReading[] = [];
        pool.forEachActive((held, handle) => {
          readings.push({
            handle,
            key: held.key,
            shown: held.shown,
            left: held.image.x,
            top: held.image.y,
            size: 32 * held.k,
            k: held.k,
            scale: held.scale,
            alpha: held.alpha,
            depth: held.image.depth,
            inUiLayer: held.image.displayList === context.layer,
            phase: held.phase,
            copies: held.copies,
          });
        });
        return readings;
      };

      /**
       * Reads every copy drawn on the last frame, for the report: made on request, never on a frame.
       *
       * @returns One reading a copy.
       */
      report.copyList = (): readonly EmojiCopyReading[] => {
        const readings: EmojiCopyReading[] = [];
        pool.forEachActive((held, handle) => {
          for (let index = 0; held.pop !== 0 && index < EMOJI_COPIES; index += 1) {
            const slot = (held.copyFirst + index) % ring.size;
            const copy = ring.sprite(slot);
            if (ring.owns(slot, held.pop) && copy.shown) {
              readings.push({ handle, left: copy.image.x, top: copy.image.y, size: 32 * copy.k, k: copy.k, scale: copy.scale, alpha: copy.alpha, depth: copy.image.depth });
            }
          }
        });
        return readings;
      };

      return {
        /**
         * Takes the emoji shown since the last frame, visits every character drawn this frame while any emoji is held,
         * then ends the emoji no visit saw or whose time ran out. With the switch off, or no local character drawn,
         * nothing is visited and every emoji ends.
         *
         * @param time The scene's time in ms, which an emoji's life is counted on.
         */
        update(time) {
          frameNumber += 1;
          now = time;
          reduced = motion.reduced();
          shown = 0;
          copiesShown = 0;
          report.take(start);
          if (report.enabled && pool.active > 0 && context.characters.local(local)) {
            ownHandle = context.characters.localHandle();
            context.characters.forEach(visit);
          }
          pool.forEachActive(sweep);
          report.active = pool.active;
          report.created = pool.created;
          report.shown = shown;
          report.copies = copiesShown;
          report.copySprites = ring.created;
        },
        /**
         * Stops following the page's icons and forgets the report's list, which would hold the destroyed sprites.
         */
        destroy() {
          unsubscribe();
          /**
           * Lists no emoji, since none is held once the scene has shut down.
           *
           * @returns No readings.
           */
          report.list = (): readonly EmojiReading[] => [];
          /**
           * Lists no copies, since none is drawn once the scene has shut down.
           *
           * @returns No readings.
           */
          report.copyList = (): readonly EmojiCopyReading[] => [];
        },
      };
    },
  };
}
